<template>
  <BaseModal ref="modal">
    <UiModalDialog
      class="jps-image-upload-modal"
      dialog-width="40rem"
      dialog-max-height="calc(100vh - 2rem)"
    >
      <template #header>
        <BaseIcon name="mdi/upload" />
        {{ ls(modalTitle) }}
      </template>
      <div class="jps-modal-content">
        <ValidationForm
          v-if="showMeta"
          :rule="rule"
          :target="_content"
          @valid="(v) => contentValid = v"
          except="id,images"
        />
        <div class="jps-field" v-if="showFile">
          <label class="jps-label" for="im-select">画像ファイル（JPG/PNG）</label>
          <BaseUpload
            id="im-select"
            accept="image/jpeg, image/png"
            :image="true"
            :multiple="!_single"
            v-model="files"
          />
          <p>※サーバ上でJPEGに変換されるため、透過は利用できません。</p>
        </div>
      </div>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="submit()" :disabled="!submitOk">{{ ls(dapLangGlobal.ok) }}</button>
      </template>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal } from '#components'

const { ls } = useI18n()
const { isLoading, setLoading } = useLoader()
const { snackbarError } = useSnackbar()
const { createContentMeta, addImage } = useContent()

interface Props {
  mode?: 'new' | 'meta' | 'image'
  content?: Content
  single?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  single: false
})

const modal = ref<InstanceType<typeof BaseModal>>()
const _mode: Ref<'new' | 'meta' | 'image'> = ref()
const _content: Ref<Content> = ref()
const _single: Ref<boolean> = ref()
const openAwait = async (options?: Props) => {
  setOptionsValue(_mode, 'mode', props, options)
  setOptionsValue(_content, 'content', props, options)
  setOptionsValue(_single, 'single', props, options)
  if (!_content.value) {
    _content.value = {}
  }
  files.value = []
  return await modal.value.openAwait<File[] | Content>()
}
defineExpose({ openAwait })

const rule: ValidationRule = CONTENT_RULE
const files: Ref<File[]> = ref([])

const modalTitle = computed(() => {
  switch (_mode.value) {
    case 'new':
      return { ja: '画像のアップロード' }
    case 'meta':
      return { ja: '画像情報の編集' }
    case 'image':
      return { ja: '画像の選択' }
  }
})

const contentValid:Ref<boolean> = ref(false)

const showFile = computed(() => {
  return _mode.value !== 'meta'
})

const showMeta = computed(() => {
  return _mode.value !== 'image'
})

const submitOk = computed(() => {
  if (_mode.value === 'new')
    return files.value && files.value.length > 0 && contentValid.value
  if (_mode.value === 'image') return files.value && files.value.length > 0
  if (_mode.value === 'meta') return contentValid.value
})

const submit = async () => {
  if (_mode.value === 'image') {
    modal.value.resolve(files.value)
  }
  if (_mode.value === 'new') {
    if (submitOk.value && !isLoading()) {
      setLoading(true)
      try {
        _content.value = await createContentMeta(_content.value)
        for (let file of files.value) {
          try {
            _content.value = await addImage(
              _content.value.id,
              file
            )
          } catch (e) {
            console.error(e)
            snackbarError({ ja: `${file.name}の登録に失敗しました。` })
          }
        }
        modal.value.resolve(_content.value)
      } catch (e) {
        snackbarError({ ja: '登録に失敗しました。' })
      }
      setLoading(false)
    }
  }
  if (_mode.value === 'meta') {
    if (submitOk.value) {
      modal.value.resolve(_content.value)
    }
  }
}
</script>
